import { CropSettings } from "@/lib/types";

/**
 * Helper function that handles the canvas cropping logic
 */
const cropImageToCanvas = (
  image: HTMLImageElement,
  croppedAreaPixels: { x: number; y: number; width: number; height: number }
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const file = new File([blob], 'cropped-image.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        resolve(file);
      },
      'image/jpeg',
      0.95
    );
  });
};

/**
 * Creates a canvas element and draws the cropped image
 */
export const getCroppedImg = (
  imageSrc: string,
  croppedAreaPixels: { x: number; y: number; width: number; height: number },
  enableCors = false
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (enableCors) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => cropImageToCanvas(image, croppedAreaPixels).then(resolve).catch(reject);
    image.onerror = reject;
    image.src = imageSrc;
  });
};

/**
 * Creates a canvas element and draws the cropped image from a URL
 */
export const getCroppedImgFromUrl = (
  imageUrl: string,
  croppedAreaPixels: { x: number; y: number; width: number; height: number }
): Promise<File> => {
  return getCroppedImg(imageUrl, croppedAreaPixels, true);
};

/**
 * Applies saved crop settings to recreate the cropped image
 */
export const applyCropSettings = async (
  originalImageUrl: string,
  cropSettings: CropSettings,
  enableCors = false
): Promise<File> => {
  return getCroppedImg(originalImageUrl, cropSettings, enableCors);
};


/**
 * Converts a target ratio to CropSettings for an image with given dimensions
 * @param targetRatio - The desired aspect ratio (width/height)
 * @param imageWidth - The original image width
 * @param imageHeight - The original image height
 * @returns CropSettings object with centered crop maintaining the target ratio
 */
export const ratioToCropSettings = (
  targetRatio: number,
  imageWidth: number,
  imageHeight: number
): CropSettings => {
  const imageRatio = imageWidth / imageHeight;

  let cropWidth: number;
  let cropHeight: number;

  if (imageRatio > targetRatio) {
    // Image is wider than target ratio, crop width
    cropHeight = imageHeight;
    cropWidth = imageHeight * targetRatio;
  } else {
    // Image is taller than target ratio, crop height
    cropWidth = imageWidth;
    cropHeight = imageWidth / targetRatio;
  }

  // Center the crop
  const x = (imageWidth - cropWidth) / 2;
  const y = (imageHeight - cropHeight) / 2;

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(cropWidth),
    height: Math.round(cropHeight)
  };
};